/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { AbLocalToolbar, AbLocalToolbarGroup } from "abreact/incubator/ui/toolbar/AbLocalToolbar";
import { AbMarkdownExtensions } from "abreact/input/markdown/AbMarkdownExtensions";
import { AbMarkdownLinkDialog } from "abreact/input/markdown/AbMarkdownLinkDialog";
import { $createAbLinkNode, $isAbLinkNode } from "abreact/input/markdown/nodes/AbLinkNode";
import { AbButton } from "abreact/misc/AbButton";
import { AbSize } from "abreact/misc/AbSize";
import { URLUtils } from "abweb/utils/URLUtils";
import { $createParagraphNode, $createTextNode, $getRoot, $getSelection, $isLineBreakNode, $isNodeSelection, $isRangeSelection, CLICK_COMMAND, COMMAND_PRIORITY_CRITICAL, COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_LOW, KEY_ESCAPE_COMMAND, SELECTION_CHANGE_COMMAND } from 'lexical';
import { useCallback, useEffect, useState } from 'react';
import * as React from 'react';
import { getSelectedNode } from '../utils/getSelectedNode';
export function replaceLinkText(editor, text, url) {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      // if selection is the text or the image in the link, find the link by walking upward.
      let oldLink = $findMatchingParent(node, $isLinkNode);
      // if selection is itself the link, we found it.
      if ($isLinkNode(node)) {
        oldLink = node;
      }
      if (!oldLink) return;
      if (url === "") {
        oldLink.remove();
        return;
      }
      const textChildren = oldLink.getAllTextNodes();
      if (textChildren.length > 0) {
        const firstChild = textChildren[0]; // old text node
        const newText = $createTextNode(text); // new text node
        firstChild.replace(newText);
        // remove remaining text nodes (in reverse order)
        for (let i = textChildren.length - 1; i > 0; i--) {
          textChildren[i].remove();
        }
      } else if (oldLink.getChildrenSize() === 1 && $isAbLinkNode(oldLink.getFirstChild())) {
        const oldAbLink = oldLink.getFirstChild();
        const newAbLink = $createAbLinkNode(text, url);
        oldAbLink.replace(newAbLink);
      }
    } else if ($isNodeSelection(selection)) {
      const nodes = selection.getNodes();
      if (nodes.length === 1 && $isAbLinkNode(nodes[0])) {
        const oldAbLink = nodes[0];
        const oldParent = oldAbLink.getParentOrThrow();
        if (url === "") {
          oldParent.remove();
          return;
        }
        const newAbLink = $createAbLinkNode(text, url);
        oldAbLink.replace(newAbLink);
        oldParent.setURL(url);
      }
    }
  });
}
function replaceAbLinkWithText(editor) {
  editor.update(() => {
    const selection = $getSelection();
    const nodes = selection?.getNodes();
    if (nodes?.length !== 1) return; // selection must contain one node
    // Get the link: are we on the abLink, or immediately after it?
    const abLink = $isAbLinkNode(nodes[0]) ? nodes[0] : $isAbLinkNode(nodes[0].getPreviousSibling()) ? nodes[0].getPreviousSibling() : null;
    if (!abLink) return; // couldn't find the abLink
    const parent = abLink.getParentOrThrow();
    const newText = $createTextNode(abLink.getTextContent());
    if ($isLinkNode(parent)) {
      // abLink is inside a link, so replace parent link too.
      if (parent.getParent() === $getRoot()) {
        // can't insert text into the root, so wrap it with a paragraph.
        const newPara = $createParagraphNode().append(newText);
        parent.insertBefore(newPara);
      } else {
        parent.insertBefore(newText);
      }
      parent.remove();
    } else {
      // abLink is by itself, so only replace it.
      abLink.insertBefore(newText);
      abLink.remove();
    }
  });
}
export function createToolbar(setShowLinkDialog, editor, extraActionsToolbarGroup) {
  return /*#__PURE__*/React.createElement(AbLocalToolbar, {
    type: "stand-alone"
  }, /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, /*#__PURE__*/React.createElement(AbButton, {
    onClick: () => setShowLinkDialog(true),
    label: AbReactResources.markdownEditor_editLink(),
    size: AbSize.SMALL
  }), /*#__PURE__*/React.createElement(AbButton, {
    onClick: () => {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      replaceAbLinkWithText(editor);
    },
    label: AbReactResources.markdownEditor_deleteLink(),
    size: AbSize.SMALL
  })), extraActionsToolbarGroup);
}
export function useExtraActions(linkUrl) {
  const [extraActions, setExtraActions] = useState([]);
  useEffect(() => {
    const protocol = URLUtils.getProtocol(linkUrl);
    const extension = protocol ? AbMarkdownExtensions.markdownLinkExtensions.get(protocol) : undefined;
    let actions = [];
    if (extension) {
      for (let action of extension.actions) {
        const handler = _event => action.onClick(linkUrl);
        actions.push(/*#__PURE__*/React.createElement(AbButton, {
          key: action.label,
          onClick: handler,
          label: action.label
        }));
      }
    } else {
      // no extension applies, so assume it's a web link, and add "jump".
      actions.push(/*#__PURE__*/React.createElement(AbButton, {
        key: "jump",
        onClick: () => {
          window.open(linkUrl, '_blank');
        },
        label: AbReactResources.markdownEditor_jumpToTarget()
      }));
    }
    setExtraActions(actions);
  }, [linkUrl]);
  return extraActions;
}
function FloatingLinkEditor({
  editor,
  isLink,
  setIsLink,
  anchorElem
}) {
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [popupElem, setPopupElem] = useState(undefined);
  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);
      if (linkParent) {
        setLinkUrl(linkParent.getURL());
        setLinkText(linkParent.getTextContent());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
        setLinkText(node.getTextContent());
      } else {
        setLinkUrl('');
        setLinkText('');
      }
    }
    if (isLink) {
      let node = window.getSelection()?.focusNode;
      while (node != null) {
        // eslint-disable-next-line abinitio/no-instanceof
        if (node instanceof HTMLElement && node.tagName === 'A') {
          setPopupElem(node);
          return;
        }
        node = node.parentElement;
      }
      setPopupElem(undefined); // couldn't find the surrounding "A" element.
    } else setPopupElem(undefined);
  }, [isLink]);
  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;
    const update = () => {
      editor.getEditorState().read(() => {
        updateLinkEditor();
      });
    };
    window.addEventListener('resize', update);
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }
    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [anchorElem.parentElement, editor, updateLinkEditor]);
  useEffect(() => {
    return mergeRegister(editor.registerUpdateListener(({
      editorState
    }) => {
      editorState.read(() => {
        updateLinkEditor();
      });
    }), editor.registerCommand(SELECTION_CHANGE_COMMAND, () => {
      updateLinkEditor();
      return true;
    }, COMMAND_PRIORITY_LOW), editor.registerCommand(KEY_ESCAPE_COMMAND, () => {
      if (isLink) {
        setIsLink(false);
        return true;
      }
      return false;
    }, COMMAND_PRIORITY_HIGH));
  }, [editor, updateLinkEditor, setIsLink, isLink]);
  const extraActions = useExtraActions(linkUrl);
  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);
  let extraActionsToolbarGroup = extraActions.length > 0 ? /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, extraActions) : null;
  if (!isLink || popupElem === undefined || !editor.isEditable()) return /*#__PURE__*/React.createElement(React.Fragment, null);
  const toolbar = createToolbar(setShowLinkDialog, editor, extraActionsToolbarGroup);
  return /*#__PURE__*/React.createElement(AbPopup, {
    className: "ab-markdown-popup-toolbar",
    origin: popupElem,
    positioner: PopupUtils.VERTICAL_FIXED_SIZE_POSITIONER,
    overlay: false,
    flushContent: true,
    onClose: () => {
      setPopupElem(undefined);
    },
    onBackgroundCaptureMouseDown: () => {
      setPopupElem(undefined);
    }
  }, toolbar, showLinkDialog && /*#__PURE__*/React.createElement(AbMarkdownLinkDialog, {
    text: linkText,
    url: linkUrl,
    buttonEl: null,
    onOk: (text, url) => {
      replaceLinkText(editor, text, url);
      // if there's a new link, tell the editor.  If it's removed, no need.
      if (url !== "") editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
      return Promise.resolve(true);
    },
    onClose: () => {
      setShowLinkDialog(false); // turn off the link dialog
      setIsLink(false); // turn off the toolbar
    }
  }));
}
function useFloatingLinkEditorToolbar(editor, anchorElem) {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);
  useEffect(() => {
    function updateToolbar() {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const focusNode = getSelectedNode(selection);
        const focusLinkNode = $findMatchingParent(focusNode, $isLinkNode);
        const focusAutoLinkNode = $findMatchingParent(focusNode, $isAutoLinkNode);
        if (!(focusLinkNode || focusAutoLinkNode)) {
          setIsLink(false);
          return;
        }
        const badNode = selection.getNodes().filter(node => !$isLineBreakNode(node)).find(node => {
          const linkNode = $findMatchingParent(node, $isLinkNode);
          const autoLinkNode = $findMatchingParent(node, $isAutoLinkNode);
          return focusLinkNode && !focusLinkNode.is(linkNode) || linkNode && !linkNode.is(focusLinkNode) || focusAutoLinkNode && !focusAutoLinkNode.is(autoLinkNode) || autoLinkNode && !autoLinkNode.is(focusAutoLinkNode);
        });
        if (!badNode) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      }
    }
    return mergeRegister(editor.registerUpdateListener(({
      editorState
    }) => {
      editorState.read(() => {
        updateToolbar();
      });
    }), editor.registerCommand(SELECTION_CHANGE_COMMAND, (_payload, newEditor) => {
      updateToolbar();
      setActiveEditor(newEditor);
      return false;
    }, COMMAND_PRIORITY_CRITICAL), editor.registerCommand(CLICK_COMMAND, payload => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection);
        const linkNode = $findMatchingParent(node, $isLinkNode);
        if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
          window.open(linkNode.getURL(), '_blank');
          return true;
        }
      }
      return false;
    }, COMMAND_PRIORITY_LOW));
  }, [editor]);
  return /*#__PURE__*/React.createElement(FloatingLinkEditor, {
    editor: activeEditor,
    isLink: isLink,
    anchorElem: anchorElem,
    setIsLink: setIsLink
  });
}
export default function FloatingLinkEditorPlugin({
  anchorElem = document.body
}) {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(editor, anchorElem);
}
//# sourceMappingURL=FloatingLinkEditorPlugin.js.map
